"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Dropdown = void 0;
var _react = _interopRequireWildcard(require("react"));
var _useToggler2 = require("@cbhq/cds-common/hooks/useToggler");
var _menu = require("@cbhq/cds-common/tokens/menu");
var _selectContext = require("../controls/selectContext");
var _useBoundingClientRect = require("../hooks/useBoundingClientRect");
var _useBreakpoints2 = require("../hooks/useBreakpoints");
var _useIsoEffect = require("../hooks/useIsoEffect");
var _useSpacingValue = require("../hooks/useSpacingValue");
var _FocusTrap = require("../overlays/FocusTrap");
var _ModalWrapper = require("../overlays/Modal/ModalWrapper");
var _Popover = require("../overlays/popover/Popover");
var _browser = require("../utils/browser");
var _DropdownContent = require("./DropdownContent");
const _excluded = ["children", "onOpenMenu", "onCloseMenu", "content", "disablePortal", "accessibilityLabel", "value", "disableCloseOnOptionChange", "visible", "onChange", "width", "disabled", "controlledElementAccessibilityProps"],
  _excluded2 = ["content", "showOverlay", "children", "visible", "onCloseMenu", "onOpenMenu", "width", "minWidth", "maxWidth", "value", "disableCloseOnOptionChange", "testID", "maxHeight", "onChange", "disablePortal", "onBlur", "contentPosition", "block", "disabled"],
  _excluded3 = ["children", "maxHeight", "enableMobileModal", "onOpenMenu", "onCloseMenu"];
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const ModalDropdown = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let children = _ref.children,
    onOpenMenu = _ref.onOpenMenu,
    onCloseMenu = _ref.onCloseMenu,
    content = _ref.content,
    disablePortal = _ref.disablePortal,
    accessibilityLabel = _ref.accessibilityLabel,
    value = _ref.value,
    disableCloseOnOptionChange = _ref.disableCloseOnOptionChange,
    visible = _ref.visible,
    onChange = _ref.onChange,
    width = _ref.width,
    disabled = _ref.disabled,
    controlledElementAccessibilityProps = _ref.controlledElementAccessibilityProps,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const context = (0, _react.useMemo)(() => ({
    onChange,
    value,
    handleCloseMenu: !disableCloseOnOptionChange ? onCloseMenu : undefined
  }), [onChange, value, disableCloseOnOptionChange, onCloseMenu]);
  (0, _react.useImperativeHandle)(ref, () => ({
    openMenu: onOpenMenu,
    closeMenu: onCloseMenu
  }), [onOpenMenu, onCloseMenu]);
  return /*#__PURE__*/_react.default.createElement(_selectContext.SelectProvider, {
    value: context
  }, /*#__PURE__*/_react.default.createElement(_ModalWrapper.ModalWrapper, _extends({
    dangerouslyDisableResponsiveness: true,
    disablePortal: disablePortal,
    onOverlayPress: onCloseMenu,
    testID: "dropdown-modal",
    visible: visible
  }, controlledElementAccessibilityProps), /*#__PURE__*/_react.default.createElement(_FocusTrap.FocusTrap, {
    disableAutoFocus: !!value,
    onEscPress: onCloseMenu
  }, /*#__PURE__*/_react.default.createElement(_DropdownContent.DropdownContent, props, content))), /*#__PURE__*/_react.default.createElement("div", {
    onClick: disabled ? undefined : onOpenMenu,
    onKeyDown: onOpenMenu,
    style: {
      width
    }
  }, children));
}));
const defaultPopoverContentPositionConfig = {
  gap: 0.5,
  placement: 'bottom-start'
};
const useResponsiveHeight = ({
  gap,
  dropdownRef,
  maxHeight,
  visible
}) => {
  var _getBrowserGlobals;
  const dropdownHeight = (0, _react.useRef)(undefined);
  const dropdownRect = (0, _useBoundingClientRect.useBoundingClientRect)(dropdownRef);
  const bottomGutter = (0, _useSpacingValue.useSpacingValue)(2);
  const calculatedGap = (0, _useSpacingValue.useSpacingValue)(gap !== null && gap !== void 0 ? gap : 0);
  const calculatedMaxHeight = typeof maxHeight === 'number' ? maxHeight : 0;
  const _useState = (0, _react.useState)(!(0, _browser.isSSR)() ? (_getBrowserGlobals = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals === void 0 ? void 0 : _getBrowserGlobals.window.innerHeight : undefined),
    windowHeight = _useState[0],
    setWindowHeight = _useState[1];
  const handleWindowSizeChange = (0, _react.useCallback)(() => {
    var _getBrowserGlobals2;
    setWindowHeight((_getBrowserGlobals2 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals2 === void 0 ? void 0 : _getBrowserGlobals2.window.innerHeight);
  }, [setWindowHeight]);
  (0, _react.useEffect)(() => {
    var _getBrowserGlobals3;
    // useEffect will only run client side
    (_getBrowserGlobals3 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals3 === void 0 ? void 0 : _getBrowserGlobals3.window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      var _getBrowserGlobals4;
      (_getBrowserGlobals4 = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals4 === void 0 ? void 0 : _getBrowserGlobals4.window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, [handleWindowSizeChange]);
  const verticalBreakpoint = (0, _react.useMemo)(() => dropdownRect ? dropdownRect.top + calculatedMaxHeight + bottomGutter + calculatedGap : undefined, [bottomGutter, calculatedGap, calculatedMaxHeight, dropdownRect]);
  const responsivePopoverMenuHeight = (0, _react.useMemo)(() => dropdownRect ? `calc(100vh - ${dropdownRect.top}px - ${bottomGutter}px)` : undefined, [dropdownRect, bottomGutter]);
  (0, _useIsoEffect.useIsoEffect)(() => {
    if (windowHeight && verticalBreakpoint && visible && windowHeight <= verticalBreakpoint) {
      // only apply a responsive menu height if the viewport height encroaches on the menu
      dropdownHeight.current = responsivePopoverMenuHeight;
    } else {
      dropdownHeight.current = undefined;
    }
  }, [windowHeight, verticalBreakpoint, responsivePopoverMenuHeight, visible]);
  return {
    dropdownHeight: dropdownHeight.current
  };
};
const PopoverDropdown = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)((_ref2, ref) => {
  let content = _ref2.content,
    showOverlay = _ref2.showOverlay,
    children = _ref2.children,
    visible = _ref2.visible,
    onCloseMenu = _ref2.onCloseMenu,
    onOpenMenu = _ref2.onOpenMenu,
    width = _ref2.width,
    minWidth = _ref2.minWidth,
    maxWidth = _ref2.maxWidth,
    value = _ref2.value,
    disableCloseOnOptionChange = _ref2.disableCloseOnOptionChange,
    testID = _ref2.testID,
    maxHeight = _ref2.maxHeight,
    onChange = _ref2.onChange,
    disablePortal = _ref2.disablePortal,
    onBlur = _ref2.onBlur,
    _ref2$contentPosition = _ref2.contentPosition,
    contentPosition = _ref2$contentPosition === void 0 ? defaultPopoverContentPositionConfig : _ref2$contentPosition,
    block = _ref2.block,
    disabled = _ref2.disabled,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const dropdownRef = (0, _react.useRef)(null);
  const subjectRef = (0, _react.useRef)(null);
  const subjectRect = (0, _useBoundingClientRect.useBoundingClientRect)(subjectRef);
  const _useResponsiveHeight = useResponsiveHeight({
      gap: contentPosition.gap,
      dropdownRef,
      maxHeight,
      visible
    }),
    dropdownHeight = _useResponsiveHeight.dropdownHeight;
  const context = (0, _react.useMemo)(() => ({
    onChange,
    value,
    handleCloseMenu: !disableCloseOnOptionChange ? onCloseMenu : undefined
  }), [onChange, value, disableCloseOnOptionChange, onCloseMenu]);
  const memoizedContent = (0, _react.useMemo)(() => {
    return /*#__PURE__*/_react.default.createElement(_DropdownContent.DropdownContent, {
      ref: dropdownRef,
      height: dropdownHeight,
      maxHeight: maxHeight,
      maxWidth: maxWidth,
      minWidth: minWidth,
      placement: contentPosition.placement,
      value: value,
      width: block ? subjectRect.width : width
    }, content);
  }, [block, content, dropdownHeight, maxHeight, maxWidth, minWidth, subjectRect.width, value, width, contentPosition]);
  (0, _react.useImperativeHandle)(ref, () => ({
    openMenu: onOpenMenu,
    closeMenu: onCloseMenu
  }), [onOpenMenu, onCloseMenu]);
  return /*#__PURE__*/_react.default.createElement(_selectContext.SelectProvider, {
    value: context
  }, /*#__PURE__*/_react.default.createElement(_Popover.Popover
  // DropdownContent will handle exit animation on menu blur, including pressing the subject again to close
  , _extends({
    block: block,
    content: disabled ? undefined : memoizedContent,
    contentPosition: contentPosition !== null && contentPosition !== void 0 ? contentPosition : defaultPopoverContentPositionConfig,
    disableAutoFocus: !!value,
    disablePortal: disablePortal,
    disabled: disabled,
    onBlur: onBlur,
    onClose: onCloseMenu,
    onPressSubject: !visible ? onOpenMenu : undefined,
    showOverlay: showOverlay,
    testID: testID,
    visible: disabled ? false : visible
  }, props), /*#__PURE__*/_react.default.createElement("div", {
    ref: subjectRef,
    style: {
      width: '100%'
    }
  }, children)));
}));
const Dropdown = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)((_ref3, ref) => {
  let children = _ref3.children,
    _ref3$maxHeight = _ref3.maxHeight,
    maxHeight = _ref3$maxHeight === void 0 ? _menu.dropdownMaxHeight : _ref3$maxHeight,
    enableMobileModal = _ref3.enableMobileModal,
    onOpenMenu = _ref3.onOpenMenu,
    onCloseMenu = _ref3.onCloseMenu,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const _useBreakpoints = (0, _useBreakpoints2.useBreakpoints)(),
    isPhone = _useBreakpoints.isPhone;
  const _useToggler = (0, _useToggler2.useToggler)(),
    visible = _useToggler[0],
    _useToggler$ = _useToggler[1],
    toggleOn = _useToggler$.toggleOn,
    toggleOff = _useToggler$.toggleOff;
  const handleOpenMenu = (0, _react.useCallback)(() => {
    toggleOn();
    onOpenMenu === null || onOpenMenu === void 0 ? void 0 : onOpenMenu();
  }, [onOpenMenu, toggleOn]);
  const handleCloseMenu = (0, _react.useCallback)(() => {
    toggleOff();
    onCloseMenu === null || onCloseMenu === void 0 ? void 0 : onCloseMenu();
  }, [onCloseMenu, toggleOff]);
  return isPhone && enableMobileModal ? /*#__PURE__*/_react.default.createElement(ModalDropdown, _extends({
    ref: ref,
    maxHeight: maxHeight,
    onCloseMenu: handleCloseMenu,
    onOpenMenu: handleOpenMenu,
    visible: visible
  }, props), children) : /*#__PURE__*/_react.default.createElement(PopoverDropdown, _extends({
    ref: ref,
    maxHeight: maxHeight,
    onCloseMenu: handleCloseMenu,
    onOpenMenu: handleOpenMenu,
    visible: visible
  }, props), children);
}));
exports.Dropdown = Dropdown;